/*
═══════════════════════════════════════════════════════════
НАСТРОЙКА НА ЯСНОСТЬ / CLARITY
═══════════════════════════════════════════════════════════
Автор: @Elena_evolvecode
Telegram: @na_sh_kodila
Создано: 2025-2026
Все права защищены
═══════════════════════════════════════════════════════════
*/

import { useState, useEffect } from 'react';

const STORAGE_KEY = 'clarity-progress';
const RESTART_DELAY_DAYS = 30;

const getTodayDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

const isMorning = () => {
  const hour = new Date().getHours();
  return hour >= 6;
};

const isEvening = () => {
  const hour = new Date().getHours();
  return hour >= 18;
};

const createInitialProgress = () => ({
  onboardingComplete: false,
  status: 'not_started',
  eveningPrepDate: null,
  currentDay: 1,
  maxUnlockedDay: 1,
  totalResets: 0,
  completionCount: 0,
  lastCompletionDate: null,
  days: {
    1: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
    2: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
    3: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
    4: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
    5: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
    6: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
    7: { injectionRead: false, l1Count: 0, l2Completed: false, completedDate: null },
  },
});

export const useProgress = () => {
  const [progress, setProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const initial = createInitialProgress();
        return {
          ...initial,
          ...parsed,
          days: {
            ...initial.days,
            ...parsed.days,
          },
        };
      }
    } catch (e) {
      console.error('Error loading progress:', e);
    }
    return createInitialProgress();
  });

  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (e) {
      console.error('Error saving progress:', e);
    }
  }, [progress]);

  const canStartPractice = () => {
    if (debugMode) return true;
    if (progress.status !== 'waiting_for_morning') return true;

    const today = getTodayDate();
    const prepDate = progress.eveningPrepDate;

    return prepDate && today > prepDate && isMorning();
  };

  const isDayComplete = (dayNum) => {
    const dayProgress = progress.days[dayNum];
    if (!dayProgress) return false;
    return (
      dayProgress.injectionRead &&
      dayProgress.l1Count > 0 &&
      dayProgress.l2Completed
    );
  };

  const canGoToNextDay = (currentDayNum) => {
    if (debugMode) return isDayComplete(currentDayNum) && currentDayNum < 7;
    
    if (!isDayComplete(currentDayNum)) return false;
    if (currentDayNum >= 7) return false;

    const dayProgress = progress.days[currentDayNum];
    const today = getTodayDate();

    if (dayProgress.completedDate === today) {
      return false;
    }

    return true;
  };

  const getNextDayStatus = (currentDayNum) => {
    if (currentDayNum >= 7) {
      return { canGo: false, message: 'Это последний день курса', showButton: false };
    }

    if (!isDayComplete(currentDayNum)) {
      return { canGo: false, message: null, showButton: false };
    }

    if (debugMode) {
      return { canGo: true, message: null, showButton: true };
    }

    const dayProgress = progress.days[currentDayNum];
    const today = getTodayDate();

    if (dayProgress.completedDate === today) {
      return {
        canGo: false,
        message: `День ${currentDayNum + 1} откроется в 00:00`,
        showButton: true,
      };
    }

    return { canGo: true, message: null, showButton: true };
  };

  // Расчёт дней до повторного прохождения
  const getDaysUntilRestart = () => {
    if (!progress.lastCompletionDate) return 0;
    
    const lastDate = new Date(progress.lastCompletionDate);
    const today = new Date();
    const diffTime = today - lastDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const daysRemaining = RESTART_DELAY_DAYS - diffDays;
    
    return Math.max(0, daysRemaining);
  };

  const completeOnboarding = () => {
    const today = getTodayDate();

    if (isEvening() && !debugMode) {
      setProgress(prev => ({
        ...prev,
        onboardingComplete: true,
        status: 'waiting_for_morning',
        eveningPrepDate: today,
      }));
    } else {
      setProgress(prev => ({
        ...prev,
        onboardingComplete: true,
        status: 'active',
      }));
    }
  };

  const activatePractice = () => {
    setProgress(prev => ({
      ...prev,
      status: 'active',
    }));
  };

  const incrementL1 = (dayNum) => {
    setProgress(prev => {
      const newDayProgress = {
        ...prev.days[dayNum],
        l1Count: prev.days[dayNum].l1Count + 1,
      };

      const isNowComplete =
        newDayProgress.injectionRead &&
        newDayProgress.l1Count > 0 &&
        newDayProgress.l2Completed;

      if (isNowComplete && !newDayProgress.completedDate) {
        newDayProgress.completedDate = getTodayDate();
      }

      // Проверяем, завершён ли 7-й день
      let newCompletionCount = prev.completionCount;
      let newLastCompletionDate = prev.lastCompletionDate;
      
      if (dayNum === 7 && isNowComplete && !prev.days[7].completedDate) {
        newCompletionCount = prev.completionCount + 1;
        newLastCompletionDate = getTodayDate();
      }

      return {
        ...prev,
        completionCount: newCompletionCount,
        lastCompletionDate: newLastCompletionDate,
        days: {
          ...prev.days,
          [dayNum]: newDayProgress,
        },
      };
    });
  };

  const toggleL2 = (dayNum) => {
    setProgress(prev => {
      const newDayProgress = {
        ...prev.days[dayNum],
        l2Completed: !prev.days[dayNum].l2Completed,
      };

      const isNowComplete =
        newDayProgress.injectionRead &&
        newDayProgress.l1Count > 0 &&
        newDayProgress.l2Completed;

      if (isNowComplete && !newDayProgress.completedDate) {
        newDayProgress.completedDate = getTodayDate();
      }

      // Проверяем, завершён ли 7-й день
      let newCompletionCount = prev.completionCount;
      let newLastCompletionDate = prev.lastCompletionDate;
      
      if (dayNum === 7 && isNowComplete && !prev.days[7].completedDate) {
        newCompletionCount = prev.completionCount + 1;
        newLastCompletionDate = getTodayDate();
      }

      return {
        ...prev,
        completionCount: newCompletionCount,
        lastCompletionDate: newLastCompletionDate,
        days: {
          ...prev.days,
          [dayNum]: newDayProgress,
        },
      };
    });
  };

  const markInjectionRead = (dayNum) => {
    setProgress(prev => {
      const newDayProgress = {
        ...prev.days[dayNum],
        injectionRead: true,
      };

      const isNowComplete =
        newDayProgress.injectionRead &&
        newDayProgress.l1Count > 0 &&
        newDayProgress.l2Completed;

      if (isNowComplete && !newDayProgress.completedDate) {
        newDayProgress.completedDate = getTodayDate();
      }

      // Проверяем, завершён ли 7-й день
      let newCompletionCount = prev.completionCount;
      let newLastCompletionDate = prev.lastCompletionDate;
      
      if (dayNum === 7 && isNowComplete && !prev.days[7].completedDate) {
        newCompletionCount = prev.completionCount + 1;
        newLastCompletionDate = getTodayDate();
      }

      return {
        ...prev,
        completionCount: newCompletionCount,
        lastCompletionDate: newLastCompletionDate,
        days: {
          ...prev.days,
          [dayNum]: newDayProgress,
        },
      };
    });
  };

  const goToDay = (dayNum) => {
    if (dayNum >= 1 && dayNum <= 7) {
      setProgress(prev => ({
        ...prev,
        currentDay: dayNum,
        maxUnlockedDay: Math.max(prev.maxUnlockedDay, dayNum),
      }));
    }
  };

  const incrementResets = () => {
    setProgress(prev => ({
      ...prev,
      totalResets: prev.totalResets + 1,
    }));
  };

  // Перезапуск курса (сохраняет счётчик прохождений)
  const restartCourse = () => {
    setProgress(prev => ({
      ...createInitialProgress(),
      completionCount: prev.completionCount,
      lastCompletionDate: prev.lastCompletionDate,
      totalResets: prev.totalResets,
    }));
  };

  // Полный сброс (для debug)
  const resetAllProgress = () => {
    const initial = createInitialProgress();
    setProgress(initial);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
    setDebugMode(false);
  };

  const enableDebugMode = () => {
    setDebugMode(true);
    if (progress.status === 'waiting_for_morning') {
      activatePractice();
    }
  };

  return {
    progress,
    debugMode,
    canStartPractice,
    isDayComplete,
    canGoToNextDay,
    getNextDayStatus,
    getDaysUntilRestart,
    completeOnboarding,
    activatePractice,
    incrementL1,
    toggleL2,
    markInjectionRead,
    goToDay,
    incrementResets,
    restartCourse,
    resetAllProgress,
    enableDebugMode,
  };
};
